import { useCallback, useEffect, useState } from 'react';
import { api, errorText } from '../api';
import { useAuth } from '../auth';
import { Badge, Card, Empty, Loading, Notice, SelectField, TextField } from '../components/ui';

interface Fond {
  id: string;
  owner_id: string;
  owner_name: string | null;
  kennung: string | null;
  bezeichnung: string;
  aktiv: boolean;
  erstellt_am: string;
}

export function FondsPage() {
  const { owners } = useAuth();
  const [ownerId, setOwnerId] = useState('');
  const [liste, setListe] = useState<Fond[] | null>(null);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  // Neu-Formular
  const [bezeichnung, setBezeichnung] = useState('');
  const [kennung, setKennung] = useState('');
  const [busy, setBusy] = useState(false);

  const laden = useCallback(async () => {
    if (!ownerId) {
      setListe([]);
      return;
    }
    setError('');
    try {
      setListe(await api<Fond[]>('/api/fonds', { query: { ownerId } }));
    } catch (err) {
      setError(errorText(err));
    }
  }, [ownerId]);

  useEffect(() => {
    void laden();
  }, [laden]);

  async function anlegen() {
    if (!ownerId) {
      setError('Bitte zuerst einen Auftraggeber wählen.');
      return;
    }
    if (!bezeichnung.trim()) {
      setError('Bitte eine Bezeichnung angeben.');
      return;
    }
    setBusy(true);
    setError('');
    setMessage('');
    try {
      await api('/api/fonds', {
        method: 'POST',
        body: { ownerId, bezeichnung: bezeichnung.trim(), kennung: kennung.trim() || null },
      });
      setMessage(`Fond „${bezeichnung.trim()}" angelegt.`);
      setBezeichnung('');
      setKennung('');
      await laden();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function umschalten(f: Fond) {
    setError('');
    setMessage('');
    try {
      await api(`/api/fonds/${f.id}`, { method: 'PUT', body: { aktiv: !f.aktiv } });
      await laden();
    } catch (err) {
      setError(errorText(err));
    }
  }

  return (
    <>
      {error && <Notice kind="error">{error}</Notice>}
      {message && <Notice kind="success">{message}</Notice>}

      <Card title="Fonds (Abteilungen zur Abrechnung)">
        <p className="small muted" style={{ marginTop: 0 }}>
          Fonds sind die Abteilungen/Kostenträger eines Auftraggebers. Beim Anfordern über das Portal
          gibt der Kunde den Fond an; er hängt danach am Auftrag und steht am Packtisch und in der
          Abrechnung. Gepflegt wird je Auftraggeber.
        </p>
        <SelectField
          label="Auftraggeber"
          value={ownerId}
          onChange={setOwnerId}
          options={owners}
          render={(o) => o.name}
        />
      </Card>

      {ownerId && (
        <Card title="Neuen Fond anlegen">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
            <TextField label="Kennung (optional)" value={kennung} onChange={setKennung} placeholder="z. B. 1001" />
            <TextField label="Bezeichnung" value={bezeichnung} onChange={setBezeichnung} placeholder="z. B. Referat Z 3" />
          </div>
          <button className="primary" disabled={busy} onClick={() => void anlegen()}>
            {busy ? 'Wird angelegt …' : 'Fond anlegen'}
          </button>
        </Card>
      )}

      {ownerId && (
        <Card padded={false}>
          {!liste ? (
            <Loading />
          ) : liste.length === 0 ? (
            <Empty>Für diesen Auftraggeber sind noch keine Fonds angelegt.</Empty>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Kennung</th>
                    <th>Bezeichnung</th>
                    <th>Status</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {liste.map((f) => (
                    <tr key={f.id}>
                      <td className="mono small">{f.kennung ?? '—'}</td>
                      <td>{f.bezeichnung}</td>
                      <td>
                        <Badge kind={f.aktiv ? 'ok' : 'default'}>{f.aktiv ? 'aktiv' : 'inaktiv'}</Badge>
                      </td>
                      <td className="num">
                        <button className="small" onClick={() => void umschalten(f)}>
                          {f.aktiv ? 'Deaktivieren' : 'Aktivieren'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}
    </>
  );
}
